import { ChangeEvent, FC, memo, useRef } from 'react'

import { Button, Popover, Stack, useTheme } from '@mui/material'

import { useTranslation } from 'react-i18next'

import { useMentorCloud } from 'src/contexts/MentorCloudContext'

import { useMentorCloudDeleteFile } from 'src/hooks/files/useMentorCloudDeleteFile'
import { useMentorCloudDuplicateFile } from 'src/hooks/files/useMentorCloudDuplicateFile'
import { useCopy } from 'src/hooks/useCopy'
import useModal from 'src/hooks/useModal'
import { useAuth } from 'src/hooks/useAuth'
import useFileMutation from 'src/hooks/useFileMutation'
import { useMentorCloudAddThumbnail } from 'src/hooks/files/useMentorCloudAddThumbnail'

import DeleteConfirmationModal from 'src/components/common/modals/DeleteConfirmationModal'

import { getImageUrl } from 'src/utils/getImageUrl'

import fileService from 'src/service/file-service'

import { MentorCloudRenameModal } from '../MentorCloudRenameModal/MentorCloudRenameModal'

import { mentorCloudExtensions, MentorCloudFile, MentorCloudFileFilters } from 'src/types/files/file'

interface MentorCloudFilePopoverProps {
  parentFolderId?: number
  filters: MentorCloudFileFilters
  id?: string
  isOpen: boolean
  anchor: Element | null
  file: MentorCloudFile | null
  close: () => void
  onChangePopoverFile: (file: null) => void
}

export const MentorCloudFilePopover: FC<MentorCloudFilePopoverProps> = memo(props => {
  const { parentFolderId, id, filters, anchor, isOpen, file, close, onChangePopoverFile } = props

  const thumbnailRef = useRef<HTMLInputElement>(null)

  const { t } = useTranslation()

  const { user } = useAuth()

  const { isOpen: isDeleteModalOpen, close: closeDeleteModal, open: openDeleteModal } = useModal()
  const { isOpen: isRenameModalOpen, close: closeRenameModal, open: openRenameModal } = useModal()
  const { copyToClipboard } = useCopy()

  const theme = useTheme()
  const { deleteFile } = useMentorCloudDeleteFile(filters, parentFolderId || null)
  const { duplicateFile } = useMentorCloudDuplicateFile(filters, parentFolderId || null)

  const { onChangeUploadFiles, onUploadFilesChangeProgress } = useMentorCloud()
  const { uploadFile, isUploadPending } = useFileMutation()

  const { addThumbnailMutation, isAddThumbnailPending } = useMentorCloudAddThumbnail(filters)
  const onRenameFile = () => {
    openRenameModal()
    close()
  }

  const onCopyLink = () => {
    if (!file) return

    const url = getImageUrl(file.file_id)
    copyToClipboard(url)

    close()
    onChangePopoverFile(null)
  }

  const onDownloadFile = async () => {
    if (!file) return

    const url = fileService.getImageSource(file.file_id || '', user?.token || '')

    window.open(url)

    close()
    onChangePopoverFile(null)
  }

  const onDuplicateFile = async () => {
    close()
    if (!file) return

    const { id: _, ...rest } = file
    await duplicateFile({
      values: rest
    })
    onChangePopoverFile(null)
  }

  const onUploadThumbnail = async (e: ChangeEvent<HTMLInputElement>) => {
    const thumbnail = e.target.files?.[0]
    const fileId = file?.file_id

    if (!thumbnail || !fileId) return

    await uploadFile(
      {
        file: thumbnail,
        parentFolderId,
        onProgress: progress => onUploadFilesChangeProgress?.(thumbnail, progress)
      },
      {
        onSuccess: data => {
          addThumbnailMutation({ preview_file_id: data.data.result.file_id, video_id: fileId })
        },
        onSettled: () => {
          thumbnailRef.current!.value = ''

          onChangeUploadFiles?.(prev => prev.filter(uploadedFile => uploadedFile.file.name !== file.name))

          close()
        }
      }
    )
  }

  const onDeleteFile = async () => {
    close()
    if (!file) return

    await deleteFile({ media_file_id: file.id })
    onChangePopoverFile(null)
  }

  return (
    <>
      <MentorCloudRenameModal
        filters={filters}
        isOpen={isRenameModalOpen}
        onClose={closeRenameModal}
        fileId={file?.id}
        fileName={file?.name}
        parentFolderId={parentFolderId}
      />
      <Popover
        id={id}
        open={isOpen}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack gap={theme.spacing(1)} padding={theme.spacing(1)}>
          <Button sx={{ minWidth: theme.spacing(32) }} fullWidth onClick={onRenameFile} variant='text'>
            {t('ACTION.rename')}
          </Button>
          <Button sx={{ minWidth: theme.spacing(32) }} fullWidth onClick={onCopyLink} variant='text'>
            {t('ACTION.copy_link')}
          </Button>
          <Button sx={{ minWidth: theme.spacing(32) }} fullWidth onClick={onDownloadFile} variant='text'>
            {t('ACTION.download')}
          </Button>
          <Button sx={{ minWidth: theme.spacing(32) }} fullWidth onClick={onDuplicateFile} variant='text'>
            {t('ACTION.duplicate')}
          </Button>
          {mentorCloudExtensions.video.includes(`${file?.extension}`) && (
            <Button
              disabled={isUploadPending || isAddThumbnailPending}
              sx={{ minWidth: theme.spacing(32) }}
              fullWidth
              onClick={() => thumbnailRef.current?.click()}
              variant='text'
            >
              {t('extracted.upload_thumbnail_image')}
            </Button>
          )}
          <Button sx={{ minWidth: theme.spacing(32) }} onClick={openDeleteModal} variant='tonal' color='error'>
            {t('ACTION.delete')}
          </Button>
        </Stack>
      </Popover>
      <DeleteConfirmationModal
        entityType='file'
        confirmWord='DELETE'
        preventContextMenu
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onCancel={closeDeleteModal}
        onDelete={onDeleteFile}
      />

      <input
        type='file'
        id='thumbnail'
        ref={thumbnailRef}
        onChange={onUploadThumbnail}
        style={{ display: 'none' }}
        accept='image/*'
      />
    </>
  )
})
